// Tipi e funzioni pure della pagina API esterne (Funzioni Sviluppatore).
// I tipi rispecchiano i DTO di backend/src/routes/apiEsterne.js.

export interface ParametroEndpoint {
  nome: string; in: 'path' | 'query' | 'body'; tipo: string; obbligatorio: boolean; esempio: string; descrizione: string;
}
export interface CampoRisposta { nome: string; tipo: string; descrizione: string }
export interface ErroreEndpoint { stato: number; quando: string }
export interface EndpointCatalogo {
  metodo: string; path: string; titolo: string; descrizione: string;
  parametri: ParametroEndpoint[];
  risposta: { tipo: 'lista' | 'dettaglio'; campi: CampoRisposta[] };
  errori: ErroreEndpoint[];
  esempioRisposta: unknown;
}
export interface VoceCatalogo {
  codice: string; etichetta: string; gruppo: string; descrizione: string; endpoints: EndpointCatalogo[];
}
export type StatoChiave = 'ATTIVA' | 'REVOCATA' | 'SCADUTA';
export interface Chiave {
  id: number; nome: string; prefisso: string; stato: 'ATTIVA' | 'REVOCATA'; statoEffettivo: StatoChiave;
  scadenzaAt: string | null; revocataAt: string | null; ultimoUtilizzoAt: string | null;
  numeroChiamate: number; createdAt: string;
}
export interface Applicazione {
  id: number; societaId: number; codice: string; nome: string; descrizione: string | null;
  referente: string | null; attiva: boolean; note: string | null; createdAt: string; updatedAt: string;
  apiAbilitate: string[]; chiavi: Chiave[]; chiaviAttive: number;
}
export type EsitoChiamata = 'OK' | 'NON_AUTORIZZATA' | 'ERRORE';
export interface LogChiamata {
  id: number; createdAt: string; chiaveId: number; prefisso: string | null; codiceApi: string | null;
  metodo: string; path: string; statusCode: number; durataMs: number; ip: string | null;
  esito: EsitoChiamata; errore: string | null;
}
export interface ApplicazioneDettaglio extends Applicazione { ultimeChiamate: LogChiamata[] }
export interface ChiaveGenerata extends Chiave { chiave: string }

// Segni diacritici combinanti (U+0300–U+036F): costruito da code point per non
// dipendere da escape che gli strumenti di scrittura possono alterare.
const DIACRITICI = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

export function slugDaNome(nome: string): string {
  return nome
    .normalize('NFD').replace(DIACRITICI, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

export function raggruppaCatalogo(catalogo: VoceCatalogo[]): { gruppo: string; voci: VoceCatalogo[] }[] {
  const gruppi: { gruppo: string; voci: VoceCatalogo[] }[] = [];
  for (const v of catalogo) {
    let g = gruppi.find(x => x.gruppo === v.gruppo);
    if (!g) { g = { gruppo: v.gruppo, voci: [] }; gruppi.push(g); }
    g.voci.push(v);
  }
  return gruppi;
}

export function etichettaBreve(etichetta: string | undefined, codice = ''): string {
  if (!etichetta) return codice;
  return etichetta.replace(/\s*\([^)]*\)\s*$/, '').trim() || codice;
}

export function badgeStatoChiave(stato: StatoChiave): { classe: string; testo: string } {
  switch (stato) {
    case 'ATTIVA': return { classe: 'bop-badge bop-badge--success', testo: 'Attiva' };
    case 'REVOCATA': return { classe: 'bop-badge bop-badge--danger', testo: 'Revocata' };
    default: return { classe: 'bop-badge bop-badge--warning', testo: 'Scaduta' };
  }
}

export function badgeEsito(esito: EsitoChiamata): { classe: string; testo: string } {
  switch (esito) {
    case 'OK': return { classe: 'bop-badge bop-badge--success', testo: 'OK' };
    case 'NON_AUTORIZZATA': return { classe: 'bop-badge bop-badge--warning', testo: 'Non autorizzata' };
    default: return { classe: 'bop-badge bop-badge--danger', testo: 'Errore' };
  }
}

export function fmtDataOra(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtData(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ── Mini-swagger: URL ed esempi per i chiamanti esterni ─────────────────────

export function urlCompleta(base: string, path: string): string {
  return base.replace(/\/+$/, '') + path;
}

// URL di esempio: i parametri di path sostituiti dal loro esempio, quelli di
// query aggiunti in coda con il loro esempio.
export function urlEsempio(base: string, e: EndpointCatalogo): string {
  let path = e.path;
  const query: string[] = [];
  for (const p of e.parametri) {
    if (p.in === 'path') path = path.replace(':' + p.nome, encodeURIComponent(p.esempio));
    else if (p.in === 'query') query.push(`${p.nome}=${encodeURIComponent(p.esempio)}`);
  }
  return urlCompleta(base, path) + (query.length ? '?' + query.join('&') : '');
}

export function esempioCurl(base: string, e: EndpointCatalogo): string {
  const body = e.parametri.filter(p => p.in === 'body');
  if (e.metodo === 'GET' || body.length === 0) return `curl -H "x-api-key: $BOP_KEY" "${urlEsempio(base, e)}"`;
  const json = JSON.stringify(Object.fromEntries(body.map(p => [p.nome, p.esempio])));
  return `curl -X ${e.metodo} -H "x-api-key: $BOP_KEY" -H "Content-Type: application/json" -d '${json}' "${urlEsempio(base, e)}"`;
}
